// 📄 pages/Proyectos/components/EspecificacionTecnicaFormModal.tsx
import React, { useState } from 'react';
import { X, Save, RefreshCw, Paperclip, Trash2 } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext.tsx';
import { UploadedAttachment } from '../../../services/storageService.ts';
import { canalesDe } from './CasoPruebaFormModal.tsx';
import { SOLUCIONES } from '../solucionesConfig.ts';
import {
  EspecificacionTecnica, EspecificacionFormData, EspecificacionPrioridad, AreaResponsable,
  ESPECIFICACION_PRIORIDADES, AREAS_RESPONSABLES,
  createEspecificacion, updateEspecificacionDatos
} from '../../../services/especificacionTecnicaService.ts';

interface EspecificacionTecnicaFormModalProps {
  projectId: string;
  solucionesActivas: string[];
  // Proyecto sin SAP Ariba: el campo es "Área" (opcional) en vez de "Solución relacionada".
  modoArea?: boolean;
  especificacion?: EspecificacionTecnica | null; // presente = edición
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = (solucionInicial: string): EspecificacionFormData => ({
  titulo: '', descripcion: '', solucionRelacionada: solucionInicial, prioridad: 'Media', areaResponsable: 'Funcional'
});

export default function EspecificacionTecnicaFormModal({
  projectId, solucionesActivas, modoArea = false, especificacion, onClose, onSaved
}: EspecificacionTecnicaFormModalProps) {
  const { user, profile } = useAuth();
  const [form, setForm] = useState<EspecificacionFormData>(
    especificacion
      ? {
          titulo: especificacion.titulo, descripcion: especificacion.descripcion, solucionRelacionada: especificacion.solucionRelacionada,
          canalId: especificacion.canalId, canalNombre: especificacion.canalNombre, prioridad: especificacion.prioridad, areaResponsable: especificacion.areaResponsable
        }
      : emptyForm(modoArea ? '' : solucionesActivas[0] || '')
  );
  const [adjuntosExistentes, setAdjuntosExistentes] = useState<UploadedAttachment[]>(especificacion?.adjuntos || []);
  const [nuevosArchivos, setNuevosArchivos] = useState<File[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const opcionesCanal = canalesDe(form.solucionRelacionada);

  const handleSolucionChange = (solucion: string) => setForm({ ...form, solucionRelacionada: solucion, canalId: undefined, canalNombre: undefined });
  const handleCanalChange = (canalId: string) => {
    const canal = opcionesCanal.find((c) => c.id === canalId);
    setForm({ ...form, canalId: canalId || undefined, canalNombre: canal?.label });
  };

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setNuevosArchivos((prev) => [...prev, ...selected]);
    e.target.value = '';
  };
  const removeAdjuntoExistente = (url: string) => setAdjuntosExistentes((prev) => prev.filter((a) => a.url !== url));
  const removeNuevoArchivo = (index: number) => setNuevosArchivos((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      if (especificacion) {
        await updateEspecificacionDatos(
          projectId, especificacion.id,
          { titulo: form.titulo, descripcion: form.descripcion, prioridad: form.prioridad, areaResponsable: form.areaResponsable, adjuntosExistentes },
          nuevosArchivos
        );
      } else if (user) {
        await createEspecificacion(projectId, form, nuevosArchivos, user.uid, profile?.name || user.email || '');
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la especificación.');
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{especificacion ? 'Editar especificación técnica' : 'Nueva especificación técnica'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Título*</label>
            <input
              type="text" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="ej. Especificación de mapeo de campos custom - Factura" className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {solucionesActivas.length > 0 && (
            <div>
              <label className={labelClass}>{modoArea ? 'Área' : 'Solución*'}</label>
              <select
                value={form.solucionRelacionada}
                onChange={(e) => handleSolucionChange(e.target.value)}
                disabled={!!especificacion}
                className={`${inputClass} disabled:opacity-60`}
              >
                {modoArea && <option value="">Sin área</option>}
                {solucionesActivas.map((s) => <option key={s} value={s}>{SOLUCIONES[s]?.label || s}</option>)}
              </select>
            </div>
            )}
            {opcionesCanal.length > 0 && (
              <div>
                <label className={labelClass}>Canal</label>
                <select
                  value={form.canalId || ''}
                  onChange={(e) => handleCanalChange(e.target.value)}
                  disabled={!!especificacion}
                  className={`${inputClass} disabled:opacity-60`}
                >
                  <option value="">Sin canal específico</option>
                  {opcionesCanal.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Prioridad</label>
              <select value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value as EspecificacionPrioridad })} className={inputClass}>
                {ESPECIFICACION_PRIORIDADES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Área responsable</label>
              <select value={form.areaResponsable} onChange={(e) => setForm({ ...form, areaResponsable: e.target.value as AreaResponsable })} className={inputClass}>
                {AREAS_RESPONSABLES.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              rows={4}
              placeholder="Qué hay que especificar/actualizar, contexto del desarrollo..."
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Adjuntos (Word, PDF, capturas)</label>
            <label className="flex items-center gap-2 justify-center border border-dashed border-white/20 rounded-lg p-3 text-xs text-gray-400 hover:border-blue-400 hover:text-blue-400 cursor-pointer transition-all">
              <Paperclip className="w-4 h-4" />
              Seleccionar archivos
              <input type="file" multiple onChange={handleAddFiles} className="hidden" />
            </label>

            {(adjuntosExistentes.length > 0 || nuevosArchivos.length > 0) && (
              <div className="mt-2 space-y-1.5">
                {adjuntosExistentes.map((a) => (
                  <div key={a.url} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300">
                    <span className="truncate">{a.name}</span>
                    <button type="button" onClick={() => removeAdjuntoExistente(a.url)} className="text-gray-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {nuevosArchivos.map((f, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-gray-300">
                    <span className="truncate">{f.name}</span>
                    <button type="button" onClick={() => removeNuevoArchivo(idx)} className="text-gray-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancelar</button>
            <button
              type="submit"
              disabled={guardando || !form.titulo.trim()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
